<template>
    <div class="page-feature-track-type-rule">
        <resource-select
            :field="field"
            :loading="loadingResources"
            :value="resourceValue"
            :options="resourceOptions"
            :check-valid-rule="checkValidRule"
            :read-only="readOnly"
            :placeholder="resourcePlaceholder"
            :hide-groups="hideGroups"
            :grouped="resourceOptionsGrouped"
            @input="onInput">
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </resource-select>
        <template v-if="resourceValue">
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Condition') }}</strong>
                <div class="flex-break" />
            </template>
            <fact-select
                :options="factOptions"
                :value="factValue"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="onInput" />
            <template v-if="rule.schema === 'counter'">
                <template v-if="!isLocaleEnglish">
                    <div class="flex-break" />
                    <strong>{{ $t('Criteria') }}</strong>
                    <div class="flex-break" />
                </template>
                <simple-select
                    :options="usedOptions"
                    :value="usedValue"
                    preselect-first
                    field="used"
                    :disabled="readOnly"
                    :check-valid-rule="checkValidRule"
                    @input="onInput" />
                <component
                    :is="valueComponent.component"
                    v-if="valueComponent"
                    v-bind="valueComponent.props"
                    :read-only="readOnly"
                    :check-valid-rule="checkValidRule"
                    @input="onInput" />
            </template>
            <template v-if="rule.schema === 'aggregation'">
                <aggregation-rule
                    :rule="rule"
                    :config="config"
                    :resources="resources"
                    :read-only="readOnly"
                    :check-valid-rule="checkValidRule"
                    :is-locale-english="isLocaleEnglish"
                    :include-page-parameter="includePageParameter"
                    :include-location-data="includeLocationData"
                    :include-server-data="includeServerData"
                    :metadata="metadata"
                    :historical-metadata="historicalMetadata"
                    :session-properties="sessionProperties"
                    @input="onInput"
                    @bulk-input="onBulkInput" />
            </template>
        </template>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import get from 'lodash/get';
import AggregationRule from '@/segments/components/segment-editor-vue/aggregation-rule.vue';

import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import FactSelect from '@/segments/components/segment-editor-vue/components/fact-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';

import {
    getFactOptions,
    formatFactOptions,
    getFactValue,
    getNumberConfig,
    getRelativeDateRangeConfig,
    getSingleDateConfig,
    getAbsoluteDateRangeConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { getNoResourcesFoundText } from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

export default {
    name: 'PageFeatureTrackType',
    components: {
        AggregationRule,
        ResourceSelect,
        SingleDate,
        NumberInput,
        RelativeDateRange,
        AbsoluteDateRange,
        FactSelect,
        SimpleSelect,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        hideGroups: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
        includePageParameter: {
            type: Boolean,
            default: false,
        },
        includeLocationData: {
            type: Boolean,
            default: false,
        },
        includeServerData: {
            type: Boolean,
            default: false,
        },
        metadata: {
            type: Object,
            default: () => ({}),
        },
        historicalMetadata: {
            type: Object,
            default: () => ({}),
        },
        sessionProperties: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['input', 'bulk-input'],
    computed: {
        ...mapState({
            coreEvents: (state) => state.coreEvents.map,
        }),
        field () {
            return {
                page: 'pageId',
                feature: 'featureId',
                trackType: 'trackTypeId',
            }[this.rule.id];
        },
        resourcePlaceholder () {
            return {
                page: this.$t('pageFeatureTrackTypeRule.Select Page'),
                feature: this.$t('pageFeatureTrackTypeRule.Select Feature'),
                trackType: this.$t('pageFeatureTrackTypeRule.Select Track Event'),
            }[this.rule.id];
        },
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp });
        },
        resourceOptionsGrouped () {
            return this.resources.some((resource) => this.coreEvents[resource.id]);
        },
        resourceOptions () {
            if (this.resourceOptionsGrouped) {
                return this.resources.reduce(
                    (result, resource) => {
                        result[this.coreEvents[resource.id] ? 0 : 1].resources.push({
                            [this.field]: resource.id,
                            name: resource.name,
                            group: {
                                color: get(resource.group, 'color'),
                                name: get(resource.group, 'name'),
                            },
                        });

                        return result;
                    },
                    [
                        {
                            name: this.$t('pageFeatureTrackTypeRule.Core'),
                            resources: [],
                        },
                        {
                            name: this.$t('pageFeatureTrackTypeRule.Other'),
                            resources: [],
                        },
                    ],
                );
            }

            return this.resources.map((resource) => {
                return {
                    [this.field]: resource.id,
                    name: resource.name,
                    group: {
                        color: get(resource.group, 'color'),
                        name: get(resource.group, 'name'),
                    },
                };
            });
        },
        resourceValue () {
            if (this.resourceOptionsGrouped) {
                return this.resourceOptions.reduce((result, group) => {
                    if (result) {
                        return result;
                    }

                    return group.resources.find((option) => option[this.field] === this.rule[this.field]);
                }, null);
            }

            return this.resourceOptions.find((option) => option[this.field] === this.rule[this.field]);
        },
        factOptions () {
            const options = getFactOptions({
                id: this.rule.id,
                onlyAllowGuideCompatibleRules: this.config.onlyAllowGuideCompatibleRules,
                showFrustrations: !this.$productLine(PRODUCT_LINES.ADOPT),
                showAccounts: this.$productLine(PRODUCT_LINES.CORE, 'accountsUIEnabled'),
            });

            return formatFactOptions(options);
        },
        factValue () {
            return getFactValue(this.rule, this.factOptions);
        },
        usedOptions () {
            const options = [
                { used: 'ever', name: this.$t('segments.timeOptions.ever') },
                { used: 'since', name: this.$t('segments.timeOptions.since') },
                { used: 'withinLast', name: this.$t('segments.timeOptions.within last') },
            ];

            if (this.rule.fact === 'used') {
                const atLeastMostOptions = [
                    { used: 'atleast', name: this.$t('segments.timeOptions.at least') },
                    { used: 'atmost', name: this.$t('segments.timeOptions.at most') },
                ];

                return [...options, ...atLeastMostOptions];
            }

            if (this.rule.fact === 'lastused') {
                return [
                    { used: 'before', name: this.$t('segments.timeOptions.before') },
                    { used: 'since', name: this.$t('segments.timeOptions.since') },
                    { used: 'withinLast', name: this.$t('segments.timeOptions.within last') },
                    { used: '!withinLast', name: this.$t('segments.timeOptions.not within last') },
                    { used: 'between', name: this.$t('segments.timeOptions.between') },
                ];
            }

            return options;
        },
        usedValue () {
            return this.usedOptions.find((option) => option.used === this.rule.used);
        },
        valueComponent () {
            switch (this.rule.used) {
                case 'withinLast':
                case '!withinLast':
                    return getRelativeDateRangeConfig(this.rule);
                case 'since':
                case 'before':
                    return getSingleDateConfig(this.rule, 'value');
                case 'atmost':
                case 'atleast': {
                    const component = getNumberConfig(this.rule, 'value');
                    component.props.labels = { suffix: this.$t('pageFeatureTrackTypeRule.times') };

                    return component;
                }
                case 'between':
                    return getAbsoluteDateRangeConfig(this.rule);
                default:
                    return false;
            }
        },
    },
    methods: {
        onInput ($event) {
            this.$emit('input', $event);
        },
        onBulkInput (updates) {
            this.$emit('bulk-input', updates);
        },
    },
};
</script>

<style scoped lang="less">
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
